import { Box, Heading, Stack, Text } from '@chakra-ui/react'
import 'swiper/swiper-bundle.css'

import SeriesQ from '@/queries/SeriesQ'
import SessionQ from '@/queries/SessionQ'
import UserQ from '@/queries/UserQ'
import { useAppContext } from '@/client/context/app'
import { useQuery } from '@/client/context/query'
import { useUser } from '@/client/context/user'
import { type Query, SQL, type SQLFragment } from '@/client/query'

import TutorFollowButton from '@/components/Buttons/TutorFollowButton'
import SeriesList from '@/components/Series/SeriesList'
import { useInfiniteScroll } from '@/components/Session/helpers'
import SessionList from '@/components/Session/SessionList'

const PAST_SESSION_SCROLL_QUERY_KEY = 'pastLimit'

function ProfileSessionsTab({
  user,
  userIdQuery,
  upcomingSeries,
}: {
  user: Query
  userIdQuery: SQLFragment<number>
  upcomingSeries: Query[]
}): JSX.Element | null {
  const appContext = useAppContext()
  const currentUser = useUser()
  const query = useQuery()
  const [pastSessionsQueryLimit, handleLoadMorePastSessions, showAllPastSessionsPath] =
    useInfiniteScroll({
      queryKey: PAST_SESSION_SCROLL_QUERY_KEY,
    })
  const userIsTutor: boolean = user(UserQ.hasTutorRole()) ?? true
  const firstName = user(UserQ.first_name)

  const pastSessionsWhere = SessionQ.where(
    {
      tutor: userIdQuery,
      include: [
        {
          association: 'Topic',
          required: true,
          include: [{ association: 'Course', required: true }],
        },
      ],
    },
    SQL`${SessionQ.userCanViewSession(currentUser.id)}
      AND ${SessionQ.isNotSeriesSession()}
      AND ${SessionQ.end_date} < CURRENT_TIMESTAMP`
  )

  const pastSessions = query.many`${pastSessionsWhere}
        ORDER BY ${SessionQ.start_date} DESC
        LIMIT ${pastSessionsQueryLimit}`
  const pastSessionsCount = query<number>`COUNT(*) ${pastSessionsWhere}` ?? 0

  const pastSeries = query.many(
    SeriesQ.where(
      {
        order: [[SeriesQ.created_at, 'desc']],
        include: [{ association: 'Course', required: true }],
      },
      SQL`${SeriesQ.userCanViewSeries(currentUser.id, { disableBucketCheck: currentUser.isLoggedOut })}
      AND ${SeriesQ.isHost({ userId: userIdQuery })}
      AND NOT EXISTS(SELECT 1 ${SessionQ.where(
        { seriesId: SeriesQ.id },
        SQL`${SessionQ.getComputedEndDate()} > CURRENT_TIMESTAMP `
      )})`
    )
  )

  if (!userIsTutor) {
    return null
  }

  return (
    <Box>
      {upcomingSeries.length === 0 && pastSessionsCount === 0 && pastSeries.length === 0 ? (
        <Box bg="bg.subtle" borderRadius={8} p={12}>
          <Stack align="center">
            <Text align="center" color="fg.muted" pb={4}>
              {firstName}&#32;hasn&apos;t hosted anything yet, but you can follow them to get
              updates on future sessions.
            </Text>
            <TutorFollowButton
              tutor={user}
              followText={`Follow ${firstName}`}
              followingText={`Following ${firstName}`}
              width="auto"
            />
          </Stack>
        </Box>
      ) : (
        <>
          <Heading size="xl" as="h2" mb={6}>
            Upcoming Sessions
          </Heading>
          {upcomingSeries.length > 0 ? (
            <SeriesList series={upcomingSeries} totalItemsCount={upcomingSeries.length} />
          ) : (
            <Box
              bg="blackAlpha.50"
              borderRadius={8}
              p={12}
              border="2px dashed"
              borderColor="border.emphasized"
            >
              <Stack align="center">
                <Text align="center" color="fg.muted" pb={4}>
                  {firstName}&#32;isn&apos;t hosting anything soon, but you can follow them to get
                  updates on future sessions.
                </Text>
                <TutorFollowButton
                  tutor={user}
                  followText={`Follow ${firstName}`}
                  followingText={`Following ${firstName}`}
                  width="auto"
                />
              </Stack>
            </Box>
          )}
          <Heading size="xl" as="h1" mb={6} mt={16}>
            Past Sessions
          </Heading>
          {pastSeries.length > 0 || appContext.dryRun ? (
            <SeriesList series={pastSeries} totalItemsCount={pastSeries.length} />
          ) : null}
          {pastSessionsCount > 0 || appContext.dryRun ? (
            <SessionList
              sessions={pastSessions}
              totalItemsCount={pastSessionsCount}
              limit={pastSessionsQueryLimit}
              onLoadMore={handleLoadMorePastSessions}
              hideFooter
              showAllPath={showAllPastSessionsPath}
            />
          ) : null}
          {pastSessionsCount === 0 && pastSeries.length === 0 ? (
            <Box
              bg="bg.subtle"
              borderRadius={8}
              p={12}
              border="2px dashed"
              borderColor="border.emphasized"
            >
              <Stack align="center">
                <Text align="center" color="fg.muted" pb={4}>
                  {firstName}&#32;doesn&apos;t have any past sessions.
                </Text>
                <TutorFollowButton
                  tutor={user}
                  followText={`Follow ${firstName}`}
                  followingText={`Following ${firstName}`}
                  width="auto"
                />
              </Stack>
            </Box>
          ) : null}
        </>
      )}
    </Box>
  )
}

export default ProfileSessionsTab
